import type { Element, ElementContent, Root, RootContent } from 'hast';
import { toHtml } from 'hast-util-to-html';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import { unified } from 'unified';
import { visit } from 'unist-util-visit';
import { resolveAiChatLink, unwrapUnsupportedAiChatLinks } from '../components/message/aiChatLinkResolver';
import { aiChatSanitizeSchema } from '../components/message/aiChatSanitizeSchema';
import { repairAiChatLinkLabels } from '../components/message/repairAiChatLinkLabels';
import type { MessageBlock } from './messageBlocks';

/**
 * Readies the sanitised tree for a document outside the app. A portal link becomes an absolute URL,
 * because a pasted `crespect://matter/5` or `/matter/5` opens nothing in Word or Outlook. A table gets
 * its gridlines as attributes, which Office honours and which need no colour.
 */
const prepareForPaste = () => (tree: Root) => {
    visit(tree, 'element', (node) => {
        if (node.tagName === 'table') {
            Object.assign(node.properties, { border: 1, cellPadding: 6, cellSpacing: 0 });
            return;
        }
        if (node.tagName !== 'a') {
            return;
        }
        const resolution = resolveAiChatLink(typeof node.properties.href === 'string' ? node.properties.href : null);
        if (resolution.kind === 'internal' || resolution.kind === 'record') {
            node.properties.href = new URL(resolution.to, window.location.origin).toString();
        }
    });
};

// The chat's own Markdown pipeline (MessageTextPart) without the rendering: the same GFM parse, link
// label repair, link unwrapping and sanitising. Raw HTML is dropped, as the chat's `skipHtml` drops it.
const pasteProcessor = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(repairAiChatLinkLabels)
    .use(remarkRehype)
    .use(unwrapUnsupportedAiChatLinks)
    .use(rehypeSanitize, aiChatSanitizeSchema)
    .use(prepareForPaste);

/** The text a syntax-tree node holds, its children's included. */
export const textOf = (node: RootContent | ElementContent): string => {
    if (node.type === 'text') {
        return node.value;
    }
    return node.type === 'element' ? node.children.map(textOf).join('') : '';
};

const isList = (node: ElementContent): node is Element =>
    node.type === 'element' && (node.tagName === 'ul' || node.tagName === 'ol');

const listLines = (list: Element, indent: string): string[] => {
    const start = typeof list.properties.start === 'number' ? list.properties.start : 1;
    const items = list.children.filter((child): child is Element => child.type === 'element' && child.tagName === 'li');

    return items.flatMap((item, index) => {
        const marker = list.tagName === 'ol' ? `${start + index}.` : '•';
        const label = item.children
            .filter((child) => !isList(child))
            .map(textOf)
            .join('')
            .trim();
        const nested = item.children.filter(isList).flatMap((child) => listLines(child, `${indent}  `));
        return [`${indent}${marker} ${label}`, ...nested];
    });
};

// Tab-separated, so the rows paste as cells into a spreadsheet or a mail table.
const tableRows = (table: Element): string => {
    const rows: string[] = [];
    visit(table, 'element', (node) => {
        if (node.tagName === 'tr') {
            rows.push(
                node.children.flatMap((cell) => (cell.type === 'element' ? [textOf(cell).trim()] : [])).join('\t')
            );
        }
    });
    return rows.join('\n');
};

const plainTextBlocks = (node: RootContent | ElementContent): string[] => {
    if (node.type !== 'element') {
        const text = node.type === 'text' ? node.value.trim() : '';
        return text ? [text] : [];
    }
    switch (node.tagName) {
        case 'hr':
            return [];
        case 'pre':
            return [textOf(node).replace(/\n$/, '')];
        case 'ul':
        case 'ol':
            return [listLines(node, '').join('\n')];
        case 'table':
            return [tableRows(node)];
        case 'blockquote':
        case 'section':
            return node.children.flatMap(plainTextBlocks);
        default: {
            const text = textOf(node).trim();
            return text ? [text] : [];
        }
    }
};

/**
 * A draft's text as it is drawn and copied: a line break inside a paragraph stays one, as a letter's sign-off needs,
 * where markdown would join the two lines. Line endings are made `\n` first, and a line already ending in a backslash
 * break is left as it is.
 */
export const draftMarkdown = (text: string) => text.replace(/\r\n?/g, '\n').replace(/([^\n\\])\n(?=[^\n])/g, '$1  \n');

/** The text a block puts on the clipboard: a text block's own, and a draft Figure's text in the Figure's place. */
export const copiedText = (block: MessageBlock): string[] => {
    if (block.kind === 'text') {
        return [block.text];
    }
    return block.kind === 'figure' && block.definition.kind === 'draft' && block.definition.text
        ? [draftMarkdown(block.definition.text)]
        : [];
};

/**
 * The two flavours the copy button puts on the clipboard: HTML for a rich-text target such as Word,
 * Outlook or OneNote, and plain text with no Markdown symbols for any other field.
 *
 * Only text is copied, the text blocks and a draft's text, so a chart's anchor or specification never reaches the
 * clipboard. Each block is converted on its own, as the chat renders it: joined first, a table followed by the text
 * after a chart can read as one more table row.
 */
export const buildMessageClipboardData = (blocks: MessageBlock[]): { html: string; plainText: string } => {
    const trees = blocks.flatMap(copiedText).map((text) => pasteProcessor.runSync(pasteProcessor.parse(text)));

    return {
        html: trees.map((tree) => toHtml(tree)).join('\n'),
        plainText: trees.flatMap((tree) => tree.children.flatMap(plainTextBlocks)).join('\n\n'),
    };
};

/**
 * Puts both flavours on the clipboard, or the plain text alone where there is no ClipboardItem or the rich write is
 * refused. The caller awaits nothing before it, which Safari needs to keep the click's clipboard permission.
 */
export const writeClipboardData = async ({ html, plainText }: { html: string; plainText: string }) => {
    try {
        await navigator.clipboard.write([
            new ClipboardItem({
                'text/html': new Blob([html], { type: 'text/html' }),
                'text/plain': new Blob([plainText], { type: 'text/plain' }),
            }),
        ]);
    } catch {
        await navigator.clipboard.writeText(plainText);
    }
};
